import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { HISTORY_UI, HISTORY_ISLAND_LABELS } from "../lib/historyMeta";
import { useSeoTags } from "../lib/useSeoTags";

const SITE = "https://islandsdaily.com";
const API  = process.env.REACT_APP_BACKEND_URL;

const URL_TO_SLUG = { aruba: "aruba", curacao: "curacao" };

const readStoredLang = () => {
  try {
    const s = localStorage.getItem("ir_lang");
    if (["en","es","nl","pap","pap_aw"].includes(s)) return s;
  } catch (_e) { /* private-mode */ }
  return "en";
};

const useReactiveLang = () => {
  const [lang, setLang] = useState(readStoredLang);
  useEffect(() => {
    const handler = () => setLang(readStoredLang());
    window.addEventListener("ir_lang_changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("ir_lang_changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  const change = useCallback((next) => {
    try { localStorage.setItem("ir_lang", next); window.dispatchEvent(new Event("ir_lang_changed")); } catch(_e){}
    setLang(next);
  }, []);
  return [lang, change];
};

// Pick the best available description for the current language. Order:
// exact match → EN → any → the "no description available" fallback.
// Auto-translated fallback carries a visible disclaimer per PRD.
const pickDescription = (photo, lang, t) => {
  const d = photo.descriptions || {};
  if (d[lang]) return { text: d[lang], is_auto: false };
  if (d.en)    return { text: d.en, is_auto: lang !== "en" };
  const firstKey = Object.keys(d)[0];
  if (firstKey) return { text: d[firstKey], is_auto: lang !== firstKey };
  return { text: t.no_description[lang] || t.no_description.en, is_auto: false };
};

// Compact photo card used across the hub + island pages. Each card
// links to /history/{slug}. Attribution is intentionally on the CARD
// (not just the detail page) so we satisfy CC-BY-SA "reasonable
// attribution" requirements even in listing views.
const PhotoCard = ({ photo, lang, t }) => {
  const desc = pickDescription(photo, lang, HISTORY_UI);
  return (
    <Link
      to={`/history/${photo.slug}`}
      className="group flex flex-col bg-white border border-[#EEE] rounded-lg overflow-hidden hover:shadow-md transition-shadow"
      data-testid={`history-photo-card-${photo.slug}`}
    >
      <div className="aspect-[4/3] bg-[#F5F5F5] overflow-hidden">
        <img
          src={photo.thumb_url || photo.image_url}
          alt={photo.commons_title || ""}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
          data-testid={`history-photo-img-${photo.slug}`}
        />
      </div>
      <div className="p-3 text-[12px] text-[#3D4C52] flex flex-col gap-1">
        <div className="text-[10px] uppercase tracking-wider text-[#6B7C82]">
          {photo.date_display || photo.year || "—"}
        </div>
        <div className="line-clamp-2 text-[#1A2E35] text-[12.5px]">{desc.text}</div>
        <div className="text-[10px] text-[#6B7C82] mt-1 truncate">
          {HISTORY_UI.meta_license[lang]}: {photo.license_short}
        </div>
      </div>
    </Link>
  );
};

const DecadeFilter = ({ decades, active, onChange, lang }) => (
  <div className="flex flex-wrap gap-2 mb-4" data-testid="history-decade-filter">
    <button
      type="button"
      onClick={() => onChange(null)}
      data-testid="history-decade-all"
      className={`text-[12px] px-3 py-1 rounded-full border ${
        active == null ? "bg-[#0AADA0] text-white border-[#0AADA0]" : "bg-white text-[#3D4C52] border-[#E5E5E5]"
      }`}
    >
      {HISTORY_UI.filter_all_decades[lang]}
    </button>
    {decades.map((d) => (
      <button
        key={d.decade}
        type="button"
        onClick={() => onChange(d.decade)}
        data-testid={`history-decade-${d.decade}`}
        className={`text-[12px] px-3 py-1 rounded-full border ${
          active === d.decade ? "bg-[#0AADA0] text-white border-[#0AADA0]" : "bg-white text-[#3D4C52] border-[#E5E5E5]"
        }`}
      >
        {(HISTORY_UI.filter_decade[lang] || "%ss").replace("%s", d.decade)} · {d.count}
      </button>
    ))}
  </div>
);

const buildCollectionSchema = (islandLabel, canonical) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: islandLabel,
  url: canonical,
});

export default function HistoryHubPage() {
  const { islandSlug } = useParams();
  const islandKey = islandSlug ? URL_TO_SLUG[islandSlug] || null : null;
  const [lang, setLang] = useReactiveLang();
  const [decade, setDecade] = useState(null);
  const [data, setData] = useState({ photos: [], total: 0, decades: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 60 };
    if (islandKey) params.island = islandKey;
    if (decade)    params.decade = decade;
    axios.get(`${API}/api/history`, { params }).then((r) => {
      if (!cancelled) { setData(r.data || {photos:[],total:0,decades:[]}); setLoading(false); }
    }).catch(() => { if (!cancelled) { setData({photos:[],total:0,decades:[]}); setLoading(false); }});
    return () => { cancelled = true; };
  }, [islandKey, decade]);

  const islandLabel = islandKey ? HISTORY_ISLAND_LABELS[islandKey] : null;
  const title = islandLabel
    ? (HISTORY_UI.island_page_title[lang] || "%s — Historical Photos").replace("%s", islandLabel)
    : HISTORY_UI.hub_title[lang];
  const description = HISTORY_UI.hub_intro[lang];
  const canonical = islandKey ? `${SITE}/${islandKey}/history` : `${SITE}/history`;

  const seoConfigs = useMemo(() => {
    const alternates = ["en","nl","es","pap"].map((h) => ({ hreflang: h, href: canonical }))
      .concat([{ hreflang: "x-default", href: canonical }]);
    const cfg = { title, description, canonical, alternates };
    return { en: cfg, nl: cfg, es: cfg, pap: cfg, pap_aw: cfg };
  }, [title, description, canonical]);
  useSeoTags(lang, seoConfigs);

  return (
    <div className="min-h-screen bg-white" data-testid={islandKey ? `history-island-${islandKey}` : "history-hub"}>
      <script
        type="application/ld+json"
        data-testid="history-collection-schema"
        dangerouslySetInnerHTML={{__html: JSON.stringify(buildCollectionSchema(title, canonical))}}
      />
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to={islandKey ? "/history" : "/"}
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="history-back-link"
          >
            <ArrowLeft size={15} /> {islandKey ? HISTORY_UI.back_all_photos[lang] : HISTORY_UI.back_home[lang]}
          </Link>
          <Link to="/" className="ml-auto font-serif-display text-[18px] leading-none">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          <LanguageToggle lang={lang} onChange={setLang} />
        </div>
      </div>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A2E35] mb-3" data-testid="history-h1">{title}</h1>
        <p className="text-[14px] text-[#3D4C52] leading-[1.7] max-w-3xl mb-6">{description}</p>
        {data.decades && data.decades.length ? (
          <DecadeFilter decades={data.decades} active={decade} onChange={setDecade} lang={lang} />
        ) : null}
        {loading ? (
          <div className="text-[13px] text-[#6B7C82]" data-testid="history-loading">Loading…</div>
        ) : data.photos.length === 0 ? (
          <div className="text-[13px] text-[#6B7C82] py-12 text-center" data-testid="history-empty">
            {HISTORY_UI.no_photos_yet[lang]}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
            data-testid="history-grid"
          >
            {data.photos.map((p) => <PhotoCard key={p.id} photo={p} lang={lang} t={HISTORY_UI} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
